import { useQuery } from '@tanstack/react-query';
import {
  ChartFrame,
  ChartTable,
  LineChart,
  PlanBars,
  PlanDonut,
  SERIES_GROWTH,
  SERIES_REVENUE,
  StatusList,
} from '../../components/charts';
import { adminApi, formatCount, formatMoney } from '../../lib/adminApi';
import { usePageTitle } from '../../lib/usePageTitle';
import { AdminPanelState, StatTile } from './AdminLayout';

/**
 * The overview screen.
 *
 * One query, not eight. The tiles, the charts and the breakdowns are read
 * together and have to agree with each other; fetched separately they would be
 * separate snapshots, and a dashboard whose total does not equal the sum of its
 * own chart is a support ticket.
 */
export function AdminDashboard() {
  usePageTitle('Dashboard — WaveQA Admin');

  const dashboard = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => adminApi.getDashboard(),
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-white">Dashboard</h1>
        <p className="mt-1 text-sm text-hull-400">
          Live figures from the platform database.
        </p>
      </div>

      <AdminPanelState loading={dashboard.isPending} error={dashboard.error}>
        {dashboard.data && (
          <div className="space-y-8">
            {dashboard.data.has_excluded_currency_payments && (
              <div
                role="status"
                className="rounded-card border border-warn-600/40 bg-warn-600/10 px-4 py-3 text-sm text-warn-400"
              >
                Some completed payments are in a currency other than{' '}
                {dashboard.data.currency} and are not included in the revenue figures
                below.
              </div>
            )}

            <section aria-labelledby="headline">
              <h2 id="headline" className="sr-only">
                Headline figures
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatTile
                  label="Total users"
                  value={formatCount(dashboard.data.total_users.value)}
                  change={dashboard.data.total_users.change_percent}
                  hint="vs 30 days ago"
                />
                <StatTile
                  label="Active users"
                  value={formatCount(dashboard.data.active_users.value)}
                  change={dashboard.data.active_users.change_percent}
                  hint="vs 30 days ago"
                />
                <StatTile
                  label="Paid users"
                  value={formatCount(dashboard.data.paid_users.value)}
                  change={dashboard.data.paid_users.change_percent}
                  hint="vs 30 days ago"
                />
                <StatTile
                  label="MRR"
                  value={formatMoney(
                    dashboard.data.mrr.minor_units,
                    dashboard.data.currency,
                    { exact: true },
                  )}
                  hint="contracted, from active paid plans"
                />
              </div>
            </section>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatTile
                label="Revenue this month"
                value={formatMoney(
                  dashboard.data.revenue_this_month.minor_units,
                  dashboard.data.currency,
                  { exact: true },
                )}
                change={dashboard.data.revenue_this_month.change_percent}
                hint="vs last month"
              />
              <StatTile
                label={`Revenue ${dashboard.data.year}`}
                value={formatMoney(
                  dashboard.data.revenue_this_year.minor_units,
                  dashboard.data.currency,
                  { exact: true },
                )}
                change={dashboard.data.revenue_this_year.change_percent}
                hint="vs last year"
              />
              <StatTile
                label="Accounts without a plan"
                value={formatCount(dashboard.data.users_without_subscription)}
                hint="signed up, never activated"
              />
              <StatTile
                label="QA runs"
                value={formatCount(dashboard.data.qa_runs_total)}
                hint="every run ever reported"
              />
            </div>

            <ChartFrame
              title={`Revenue — ${dashboard.data.year}`}
              caption="Payments confirmed by the billing provider, by month."
              table={
                <ChartTable
                  columns={['Month', 'Revenue']}
                  rows={dashboard.data.revenue_series.map((point) => [
                    point.label,
                    formatMoney(point.value, dashboard.data.currency, { exact: true }),
                  ])}
                />
              }
            >
              <LineChart
                points={dashboard.data.revenue_series}
                color={SERIES_REVENUE}
                format={(value) => formatMoney(value, dashboard.data.currency)}
                emptyNote="No confirmed payments yet this year."
              />
            </ChartFrame>

            <div className="grid gap-6 lg:grid-cols-2">
              <ChartFrame
                title="Users by plan"
                caption="Accounts occupying each plan — active and trialling."
                table={
                  <ChartTable
                    columns={['Plan', 'Accounts', 'Billing', 'Monthly revenue']}
                    rows={dashboard.data.plans.map((plan) => [
                      plan.display_name,
                      plan.subscribers,
                      plan.billing_subscribers,
                      formatMoney(plan.monthly_revenue_minor_units, dashboard.data.currency, {
                        exact: true,
                      }),
                    ])}
                  />
                }
              >
                <PlanDonut
                  slices={dashboard.data.plans.map((plan) => ({
                    label: plan.display_name,
                    value: plan.subscribers,
                  }))}
                />
              </ChartFrame>

              <ChartFrame
                title="Subscriptions"
                caption="Expired means the paid period has ended — the agent is already being refused, whatever the stored status says."
              >
                <StatusList
                  entries={[
                    {
                      label: 'Active',
                      value: dashboard.data.subscriptions.active,
                      tone: 'active',
                    },
                    {
                      label: 'Trialling',
                      value: dashboard.data.subscriptions.trialing,
                      tone: 'trialing',
                    },
                    {
                      label: 'Cancelled',
                      value: dashboard.data.subscriptions.canceled,
                      tone: 'cancelled',
                    },
                    {
                      label: 'Expired',
                      value: dashboard.data.subscriptions.expired,
                      tone: 'expired',
                    },
                    {
                      label: 'Past due',
                      value: dashboard.data.subscriptions.past_due,
                      tone: 'cancelled',
                    },
                    {
                      label: 'Awaiting payment',
                      value: dashboard.data.subscriptions.pending,
                      tone: 'neutral',
                      hint: 'Checkout started, no verified payment event yet.',
                    },
                  ]}
                />
              </ChartFrame>
            </div>

            <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
              <ChartFrame
                title={`User growth — ${dashboard.data.year}`}
                caption="Total accounts at the end of each month, so the last point matches the Total users tile."
                table={
                  <ChartTable
                    columns={['Month', 'Total users']}
                    rows={dashboard.data.user_growth_series.map((point) => [
                      point.label,
                      formatCount(point.value),
                    ])}
                  />
                }
              >
                <LineChart
                  points={dashboard.data.user_growth_series}
                  color={SERIES_GROWTH}
                  format={(value) => formatCount(Math.round(value))}
                  emptyNote="No accounts yet."
                />
              </ChartFrame>

              <ChartFrame title="Plan distribution">
                <PlanBars
                  slices={dashboard.data.plans.map((plan) => ({
                    label: plan.display_name,
                    value: plan.subscribers,
                  }))}
                />
              </ChartFrame>
            </div>
          </div>
        )}
      </AdminPanelState>
    </div>
  );
}
